import React from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import ListItem from "@mui/material/ListItem";
import { Props as ApexChartOptions } from "react-apexcharts";

import SVG from "@/assets/svg";
import { useAppSelector } from "@/redux/hooks";
import sanitizePieChartOptions from "@/utils/chart";
import { ACCESS_LEVEL } from "@/utils/constants";
import { $term } from "@/utils/helpers";
import CostumeButton from "../button";
import CustomCardComponent from "../card/customCard";
import EmptyComponent from "../emptyComponent";

const ReactApexChart = dynamic(() => import("react-apexcharts"), {
  ssr: false,
});

type Props = {
  chartData: ApexChartOptions;
  loading: boolean;
};

function DashboardChart({ chartData, loading }: Props) {
  const { data: terms } = useAppSelector((state) => state.terms.terms);
  const { user } = useAppSelector((state) => state.user);
  const safeChartData = sanitizePieChartOptions(chartData);
  const occupancyChartOptions = {
    ...safeChartData,
    // The labels are rendered in the responsive list beside the pie. Apex's
    // own legend duplicates them inside the chart and overlaps the plot at
    // constrained widths.
    legend: {
      ...safeChartData.legend,
      show: false,
    },
  };

  return (
    <CustomCardComponent title={`${terms?.serviceUser} occupancy`}>
      <Box className="dashboard-panel-content">
        <Box className="dashboard-chart-content" sx={{ minWidth: 0 }}>
          {safeChartData.series.length > 0 && !loading ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 0 }}>
                {safeChartData.labels.map((label: string, index: number) => (
                  <ListItem key={index} className="series-list">
                    <Box className="chartLabel">
                      <span
                        className={`series${index === 0 ? "" : "-" + index}`}
                      >
                        {" "}
                      </span>
                      <p className="content">{label}</p>
                    </Box>
                  </ListItem>
                ))}
              </Grid>

              <Grid
                size={{ xs: 12, sm: 6 }}
                className="dashboardchart"
                sx={{ minWidth: 0 }}
              >
                <ReactApexChart
                  options={occupancyChartOptions}
                  series={safeChartData.series}
                  type="pie"
                  height={240}
                  width="100%"
                  data-testid="dashboard-chart"
                />
              </Grid>
            </Grid>
          ) : safeChartData.series.length === 0 && !loading ? (
            <div className="no-data">
              <EmptyComponent label="No data" />
            </div>
          ) : (
            <div className="loading-container-small">
              <CircularProgress />
            </div>
          )}
        </Box>
        {[ACCESS_LEVEL.ADMINISTRATOR].includes(user.accessLevel) &&
          terms?.staff && (
            <Box className="dashboard-panel-action">
              <Link href="/service/service-user/add">
                <CostumeButton className="addMember">
                  <SVG.Plus />
                  <span className="service-memeberlist-add-new">
                    Add a {$term(terms?.serviceUser)}
                  </span>
                </CostumeButton>
              </Link>
            </Box>
          )}
      </Box>
    </CustomCardComponent>
  );
}

export default DashboardChart;
